import React, { useState, useEffect } from 'react';
import { CheckCircle, ChevronDown, Building2, User, Phone, Mail, Hash, Calendar, Search, Truck, MapPin, Navigation, FileText, AlertTriangle, Warehouse } from 'lucide-react';
import { formatDate, getNextBusinessDay, sanitizeErpText, dateYearWarning } from '../../utils/formatting';

export default function OrderHeader({ aiOrderNumber, extractedData, validationResult, selectedCustomer, requestedShipDate, onRequestedShipDateChange, fobCodes, carrierCodes, selectedFobCode, selectedCarrierCode, onFobCodeChange, onCarrierCodeChange, warehouseCodes, selectedWarehouse, onWarehouseChange, shipVia, onShipViaChange, orderNotes, onOrderNotesChange, headerDropShip, onHeaderDropShipChange, customerDropdownOpen, onCustomerDropdownToggle, onSelectCustomer, onOpenCustomerShipToSearch, editedPoNumber, poNumberWarning, onPONumberChange }) {
  const minDate = getNextBusinessDay();
  const effectivePoNumber = editedPoNumber ?? extractedData?.po_number ?? '';
  const [localPoNumber, setLocalPoNumber] = useState(effectivePoNumber);

  useEffect(() => {
    setLocalPoNumber(effectivePoNumber);
  }, [effectivePoNumber]);

  return (
    <div className="bg-slate-800 rounded-xl p-4">
      <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">Order Header</h3>
      {/* AI Order Number - prominent display */}
      {aiOrderNumber && (
        <div className="mb-3 pb-3 border-b border-slate-700">
          <label className="text-xs text-slate-500 flex items-center gap-1">
            <Hash className="w-3 h-3" /> Order Number
          </label>
          <div className="text-amber-400 font-mono text-lg font-bold">{aiOrderNumber}</div>
        </div>
      )}
      <div className="grid grid-cols-3 gap-4">
        <div>
          <label className="text-xs text-slate-500 flex items-center gap-1">
            <Hash className="w-3 h-3" /> PO Number
          </label>
          <input
            type="text"
            value={localPoNumber}
            onChange={(e) => setLocalPoNumber(e.target.value)}
            onBlur={() => {
              const trimmed = localPoNumber.trim();
              if (trimmed && trimmed !== (editedPoNumber ?? extractedData?.po_number)) {
                onPONumberChange(trimmed);
              } else if (!trimmed) {
                setLocalPoNumber(editedPoNumber ?? extractedData?.po_number ?? '');
              }
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.target.blur();
              }
            }}
            maxLength={22}
            className="mt-0.5 w-full bg-slate-700 text-white font-mono text-sm rounded px-2 py-1 border border-slate-600 focus:border-amber-500 focus:outline-none"
          />
          {poNumberWarning && (
            <div className="mt-1 flex items-center gap-1 text-amber-400 text-xs">
              <AlertTriangle className="w-3 h-3 flex-shrink-0" />
              <span>{poNumberWarning}</span>
            </div>
          )}
        </div>
        <div>
          <label className="text-xs text-slate-500 flex items-center gap-1">
            <Calendar className="w-3 h-3" /> PO Date
          </label>
          <div className="text-white">{formatDate(extractedData?.po_date) || extractedData?.po_date}</div>
          {dateYearWarning(extractedData?.po_date, 'po') && (
            <div className="mt-1 flex items-center gap-1 text-amber-400 text-xs">
              <AlertTriangle className="w-3 h-3 flex-shrink-0" />
              <span>{dateYearWarning(extractedData?.po_date, 'po')}</span>
            </div>
          )}
        </div>
        <div>
          <label className="text-xs text-slate-500 flex items-center gap-1">
            <Calendar className="w-3 h-3" /> Requested Ship Date
            {!requestedShipDate && <span className="text-red-400">*</span>}
          </label>
          <input
            type="date"
            value={requestedShipDate || ''}
            min={minDate}
            onChange={(e) => onRequestedShipDateChange(e.target.value || null)}
            className={`mt-0.5 w-full bg-slate-700 text-white text-sm rounded px-2 py-1 border ${requestedShipDate ? 'border-slate-600' : 'border-red-500'} focus:border-amber-500 focus:outline-none`}
          />
          {dateYearWarning(requestedShipDate, 'ship') && (
            <div className="mt-1 flex items-center gap-1 text-amber-400 text-xs">
              <AlertTriangle className="w-3 h-3 flex-shrink-0" />
              <span>{dateYearWarning(requestedShipDate, 'ship')}</span>
            </div>
          )}
        </div>
      </div>

      {/* FOB & Carrier Codes */}
      {(fobCodes?.length > 0 || carrierCodes?.length > 0) && (
        <div className="grid grid-cols-3 gap-4 mt-3">
          <div>
            <label className="text-xs text-slate-500 flex items-center gap-1">
              <MapPin className="w-3 h-3" /> FOB Code <span className="text-red-400" title="Required">*</span>
            </label>
            <select
              value={selectedFobCode?.code || ''}
              onChange={(e) => {
                const code = e.target.value;
                onFobCodeChange(code ? { code, source: 'manual' } : null);
              }}
              className={`mt-0.5 w-full bg-slate-700 text-white text-sm rounded px-2 py-1 border focus:border-amber-500 focus:outline-none ${
                selectedFobCode?.code ? 'border-slate-600' : 'border-red-500/60'
              }`}
            >
              <option value="">Select FOB Code</option>
              {fobCodes.map(f => (
                <option key={f.code} value={f.code}>
                  {f.code}{f.description ? ` - ${f.description}` : ''}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-xs text-slate-500 flex items-center gap-1">
              <Truck className="w-3 h-3" /> Carrier Code
            </label>
            <select
              value={selectedCarrierCode?.code || ''}
              onChange={(e) => {
                const code = e.target.value;
                onCarrierCodeChange(code ? { code, source: 'manual' } : null);
              }}
              className="mt-0.5 w-full bg-slate-700 text-white text-sm rounded px-2 py-1 border border-slate-600 focus:border-amber-500 focus:outline-none"
            >
              <option value="">Select Carrier Code</option>
              {carrierCodes.map(c => (
                <option key={c.code} value={c.code}>
                  {c.code}{c.description ? ` - ${c.description}` : ''}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-xs text-slate-500 flex items-center gap-1">
              <Warehouse className="w-3 h-3" /> Warehouse
            </label>
            <select
              value={selectedWarehouse?.code || ''}
              onChange={(e) => {
                const code = e.target.value;
                onWarehouseChange(code || null);
              }}
              className="mt-0.5 w-full bg-slate-700 text-white text-sm rounded px-2 py-1 border border-slate-600 focus:border-amber-500 focus:outline-none"
            >
              <option value="">Select Warehouse</option>
              {warehouseCodes.map(w => (
                <option key={w.code} value={w.code}>
                  {w.code}{w.description ? ` - ${w.description}` : ''}
                </option>
              ))}
            </select>
          </div>
        </div>
      )}

      {/* Ship Via */}
      <div className="mt-3">
        <label className="text-xs text-slate-500 flex items-center gap-1">
          <Navigation className="w-3 h-3" /> Ship Via
        </label>
        <input
          type="text"
          value={shipVia || ''}
          maxLength={20}
          onChange={(e) => onShipViaChange(e.target.value.slice(0, 20))}
          className="mt-0.5 w-full bg-slate-700 text-white text-sm rounded px-2 py-1 border border-slate-600 focus:border-amber-500 focus:outline-none"
        />
      </div>

      {/* Note — order header note field (distinct from the multi-line "Order Comments" section); maps to the A+ order header Note field */}
      <div className="mt-3">
        <div className="flex items-center justify-between">
          <label className="text-xs text-slate-500 flex items-center gap-1">
            <FileText className="w-3 h-3" /> Note
          </label>
          <span className="text-xs text-slate-500">{(orderNotes || '').length}/30</span>
        </div>
        <textarea
          value={orderNotes || ''}
          maxLength={30}
          rows={2}
          onChange={(e) => onOrderNotesChange(sanitizeErpText(e.target.value, 30))}
          className="mt-0.5 w-full bg-slate-700 text-white text-sm rounded px-2 py-1 border border-slate-600 focus:border-amber-500 focus:outline-none resize-none"
        />
      </div>

      {/* Drop Ship Order — header-level flag sent to A+ via cXML */}
      <div className="mt-3">
        <label className="flex items-center gap-2 cursor-pointer select-none">
          <input
            type="checkbox"
            checked={!!headerDropShip}
            onChange={(e) => onHeaderDropShipChange(e.target.checked)}
            className="w-4 h-4 rounded border-slate-500 bg-slate-700 accent-amber-500 cursor-pointer"
          />
          <span className="text-xs text-slate-300 flex items-center gap-1">
            <Truck className="w-3 h-3" /> Drop Ship Order
          </span>
        </label>
      </div>

      {/* Customer Selection */}
      <div className="mt-4">
        <label className="text-xs text-slate-500 flex items-center gap-2">
          <Building2 className="w-3 h-3" /> Customer
          {validationResult?.shipto_validations && !selectedCustomer?.manuallySelected && (
            <span className={`text-xs ${
              validationResult.shipto_validations.shipto_confidence === 'high' ? 'text-emerald-400' :
              validationResult.shipto_validations.shipto_confidence === 'medium' ? 'text-amber-400' : 'text-red-400'
            }`}>
              {validationResult.shipto_validations.shipto_confidence_icon} {Math.round(validationResult.shipto_validations.shipto_confidence_score * 100)}% match
            </span>
          )}
        </label>
        <div className="relative">
          {(selectedCustomer?.confidence ?? 1) >= 0.8 || selectedCustomer?.manuallySelected ? (
            // High confidence - show static card with green checkmark
            <div className="mt-1 p-3 bg-slate-700 rounded-lg">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-white font-medium">{selectedCustomer?.name}</div>
                  <div className="text-xs text-slate-400">#{selectedCustomer?.number}</div>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => onOpenCustomerShipToSearch('customer')}
                    className="text-xs text-slate-400 hover:text-amber-400 transition-colors flex items-center gap-1"
                  >
                    <Search className="w-3 h-3" />
                    Change
                  </button>
                  <CheckCircle className="w-5 h-5 text-emerald-400" />
                </div>
              </div>
              {validationResult?.customer_validations?.po_customer_name && validationResult?.customer_validations?.po_customer_name !== selectedCustomer?.name && (
                <div className="mt-2 text-xs text-slate-400">
                  Extracted: "{validationResult?.customer_validations?.po_customer_name}"
                </div>
              )}
            </div>
          ) : (
            // Low confidence - show dropdown button
            <>
              <button
                onClick={onCustomerDropdownToggle}
                className={`w-full text-left mt-1 p-3 rounded-lg flex items-start justify-between transition-colors ${
                  (selectedCustomer?.confidence ?? 0) >= 0.6
                    ? 'bg-amber-500/10 border border-amber-500/30'
                    : 'bg-red-500/10 border border-red-500/30'
                }`}
              >
                <div>
                  <div className="text-white font-medium">{selectedCustomer?.name}</div>
                  <div className="text-xs text-slate-400">#{selectedCustomer?.number}</div>
                  {validationResult?.customer_validations?.po_customer_name && validationResult?.customer_validations?.po_customer_name !== selectedCustomer?.name && (
                    <div className="mt-1 text-xs text-slate-400">
                      Extracted: "{validationResult?.customer_validations?.po_customer_name}"
                    </div>
                  )}
                </div>
                <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${customerDropdownOpen ? 'rotate-180' : ''}`} />
              </button>

              {customerDropdownOpen && (
                <div className="absolute top-full left-0 right-0 mt-1 bg-slate-700 rounded-lg shadow-xl z-10 max-h-64 overflow-auto">
                  {validationResult?.shipto_validations?.shipto_alternatives?.length > 0 && (
                    <>
                      <div className="px-3 py-2 text-xs text-slate-400 border-b border-slate-600 sticky top-0 bg-slate-700">
                        Select Customer
                      </div>
                      {validationResult.shipto_validations.shipto_alternatives.map((alt, idx) => (
                        <button
                          key={idx}
                          onClick={() => onSelectCustomer({
                            name: alt.shipto_customer_name,
                            number: alt.shipto_customer_number,
                            confidence: alt.confidence,
                            manuallySelected: true
                          })}
                          className="w-full text-left px-3 py-2 hover:bg-slate-600 transition-colors border-b border-slate-600/50 last:border-0"
                        >
                          <div className="flex items-start justify-between">
                            <div>
                              <div className="text-white text-sm">{alt.shipto_customer_name}</div>
                              <div className="text-xs text-slate-400">#{alt.shipto_customer_number}</div>
                            </div>
                            <span className="text-xs text-slate-500">{Math.round(alt.confidence * 100)}%</span>
                          </div>
                        </button>
                      ))}
                    </>
                  )}
                  <div className="border-t border-slate-600">
                    <button
                      onClick={() => onSelectCustomer({
                        ...selectedCustomer,
                        manuallySelected: true
                      })}
                      className="w-full text-left px-3 py-2 text-emerald-400 hover:bg-slate-600 transition-colors text-sm flex items-center gap-2"
                    >
                      <CheckCircle className="w-3.5 h-3.5" />
                      Accept Current Match
                    </button>
                    <button
                      onClick={() => onOpenCustomerShipToSearch('customer')}
                      className="w-full text-left px-3 py-2 text-amber-400 hover:bg-slate-600 transition-colors text-sm flex items-center gap-2 border-t border-slate-600/50"
                    >
                      <Search className="w-3.5 h-3.5" />
                      Search A+ Customers
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Contact Info */}
      {(extractedData?.contact_name || extractedData?.contact_phone || extractedData?.contact_email) && (
        <div className="mt-4 pt-4 border-t border-slate-700">
          <label className="text-xs text-slate-500">Contact</label>
          <div className="mt-1 space-y-1">
            {extractedData?.contact_name && (
              <div className="flex items-center gap-2 text-sm text-slate-300">
                <User className="w-3 h-3 text-slate-500" />
                {extractedData.contact_name}
              </div>
            )}
            {extractedData?.contact_phone && (
              <div className="flex items-center gap-2 text-sm text-slate-300">
                <Phone className="w-3 h-3 text-slate-500" />
                {extractedData.contact_phone}
              </div>
            )}
            {extractedData?.contact_email && (
              <div className="flex items-center gap-2 text-sm text-slate-300">
                <Mail className="w-3 h-3 text-slate-500" />
                {extractedData.contact_email}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
